import { Button, Heading, List, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { useThumbtackModalContext } from "components/thumbtack-modal";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { checkForNaStringValue, trackEvent } from "utils";

import styles from "./write-out-entry.module.scss";
import { WriteOutIcon } from "./write-out-icon";

export const WriteOutEntry = ({
  referral,
  showGallery,
}: {
  referral: KeystoneReferral;
  showGallery: () => void;
}) => {
  const { data } = useSelectedPolicy();
  const { openThumbtackModal } = useThumbtackModalContext();
  const {
    actionStatement,
    contractor,
    category,
    groupedIssueStatements,
    groupedReasonings,
    inspectionReviewAssetIds,
    issueStatement,
    locationDetail,
    reasoning,
    searchTerm,
  } = referral;

  let writeOutHeading = checkForNaStringValue(issueStatement ?? "");
  if (groupedIssueStatements?.length) {
    writeOutHeading = "Roof - Multiple issues";
  }
  const reasoningDisplay = checkForNaStringValue(reasoning ?? "");
  let reasonings: string[] = [];
  if (groupedReasonings?.length) {
    reasonings = groupedReasonings;
  } else if (reasoningDisplay) {
    reasonings = [reasoningDisplay];
  }
  const locationDisplay = checkForNaStringValue(locationDetail ?? "");
  const actionStatementDisplay = checkForNaStringValue(actionStatement ?? "");
  const validSearchTerm = checkForNaStringValue(searchTerm ?? "");
  const contractorDisplay = checkForNaStringValue(
    contractor ?? "",
  )?.toLowerCase();

  return (
    <div className={styles.writeOut}>
      <div className={styles.writeOutIcon}>
        <WriteOutIcon category={category} />
      </div>
      <Stack gap="sm">
        {writeOutHeading ? (
          <Heading size="sm">{writeOutHeading}</Heading>
        ) : null}

        {reasonings.map((reasoning, idx) => (
          <Span key={idx} color="quiet" size="sm">
            {reasoning}
          </Span>
        ))}

        <div className={styles.twoEntryLine}>
          {locationDisplay ? (
            <div>
              <Span bold>Location: </Span>
              <Span>{locationDisplay}</Span>
            </div>
          ) : null}

          {inspectionReviewAssetIds?.length ? (
            <Span bold>
              <Button
                size="xs"
                leadingIcon="ImagePlaceholder"
                appearance="link"
                data-testid="write-out-photos-button"
                onPress={() => {
                  trackEvent("Inspection write-outs: Photo gallery opened", {
                    numberOfPhotos: inspectionReviewAssetIds.length,
                    referral,
                  });
                  showGallery();
                }}
              >
                {inspectionReviewAssetIds.length} photo
                {inspectionReviewAssetIds.length > 1 ? "s" : ""}
              </Button>
            </Span>
          ) : null}
        </div>

        {actionStatementDisplay ? (
          <div className={styles.resolveBox}>
            <Span bold>How to resolve:</Span>
            <List type="ul">
              <li className={styles.listItem}>{actionStatementDisplay}</li>
            </List>

            {contractorDisplay && validSearchTerm ? (
              <Span bold>
                <Button
                  appearance="link"
                  onPress={() =>
                    openThumbtackModal(
                      data?.propertyAddress?.zip ?? "",
                      validSearchTerm,
                    )
                  }
                >
                  Find a {contractorDisplay}
                </Button>
              </Span>
            ) : null}
          </div>
        ) : null}
      </Stack>
    </div>
  );
};
